import React from 'react';
import { NavigationContainer } from '@react-navigation/native';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { ActivityIndicator, View, StyleSheet, Text } from 'react-native';
import { Icon, layout } from '@tipsy/design-system';

// Direct imports for web (no Module Federation)
import AuthProvider, { type AuthState } from 'auth/providers/AuthProvider';
import AuthNavigator from 'auth/navigation/MainNavigator';
import DashboardNavigator from 'dashboard/navigation/MainNavigator';
import WalletNavigator from 'wallet/navigation/MainNavigator';
import ProfileNavigator from 'profile/navigation/MainNavigator';

const Tab = createBottomTabNavigator();

const getOrigin = () => {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return 'http://localhost:3001';
};

const linking = {
  prefixes: [getOrigin()],
  config: {
    initialRouteName: 'Dashboard',
    screens: {
      Dashboard: {
        path: 'dashboard',
        screens: {
          Home: '',
          QRCode: 'qr-code',
        },
      },
      Wallet: {
        path: 'wallet',
        screens: {
          Wallet: '',
          VirtualCard: ':cardId',
          CardSetup: 'new',
          CardTransactions: ':cardId/transactions',
        },
      },
      Profile: {
        path: 'profile',
        screens: {
          ProfileScreen: '',
        },
      },
    },
  },
};

const authLinking = {
  prefixes: ['http://localhost:3001', 'https://app.tipsy.com'],
  config: {
    screens: {
      SignIn: 'signin',
      SignUp: 'signup',
    },
  },
};

const AppWrapper = ({ children }: { children: React.ReactNode }) => (
  <View style={styles.appWrapper}>
    <View style={styles.appContainer}>{children}</View>
  </View>
);

const SplashScreen = () => (
  <View style={styles.splash}>
    <ActivityIndicator size="large" color="#FFDD00" />
    <Text style={styles.splashText}>Tipsy</Text>
  </View>
);

const ErrorBoundaryFallback = ({ name }: { name: string }) => (
  <View style={styles.error}>
    <Text style={styles.errorText}>Failed to load {name} module</Text>
  </View>
);

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

const DashboardScreen = () => (
  <ErrorBoundary fallback={<ErrorBoundaryFallback name="Dashboard" />}>
    <DashboardNavigator />
  </ErrorBoundary>
);

const WalletScreen = () => (
  <ErrorBoundary fallback={<ErrorBoundaryFallback name="Wallet" />}>
    <WalletNavigator />
  </ErrorBoundary>
);

const ProfileScreen = () => (
  <ErrorBoundary fallback={<ErrorBoundaryFallback name="Profile" />}>
    <ProfileNavigator />
  </ErrorBoundary>
);

const MainNavigator = () => (
  <Tab.Navigator
    initialRouteName="Dashboard"
    screenOptions={{
      headerShown: false,
      tabBarActiveTintColor: '#000000',
      tabBarInactiveTintColor: '#666666',
      tabBarStyle: {
        backgroundColor: '#FFFFFF',
        height: 80,
        paddingBottom: 4,
        paddingTop: 10,
      },
      tabBarItemStyle: {
        flexDirection: 'column',
      },
      tabBarLabelStyle: {
        fontSize: 12,
        marginTop: 4,
      },
      tabBarIconStyle: {
        marginBottom: 0,
      },
    }}
  >
    <Tab.Screen
      name="Wallet"
      component={WalletScreen}
      options={{
        tabBarLabel: 'Wallet',
        tabBarIcon: ({ color, size }) => (
          <Icon name="account-balance-wallet" size={size} color={color} />
        ),
      }}
    />
    <Tab.Screen
      name="Dashboard"
      component={DashboardScreen}
      options={{
        tabBarLabel: 'Dashboard',
        tabBarIcon: ({ color, size }) => <Icon name="dashboard" size={size} color={color} />,
      }}
    />
    <Tab.Screen
      name="Profile"
      component={ProfileScreen}
      options={{
        tabBarLabel: 'Profile',
        tabBarIcon: ({ color, size }) => <Icon name="person" size={size} color={color} />,
      }}
    />
  </Tab.Navigator>
);

const OnboardingNavigator = () => <ProfileScreen />;

function App(): React.JSX.Element {
  return (
    <AppWrapper>
      <ErrorBoundary fallback={<ErrorBoundaryFallback name="App" />}>
        <AuthProvider>
          {(authState: AuthState) => {
            if (authState.isLoading) {
              return <SplashScreen />;
            }

            if (authState.isSignout) {
              return (
                <NavigationContainer linking={authLinking}>
                  <AuthNavigator />
                </NavigationContainer>
              );
            }

            if (!authState.isOnboarded) {
              return (
                <NavigationContainer>
                  <OnboardingNavigator />
                </NavigationContainer>
              );
            }

            return (
              <NavigationContainer linking={linking}>
                <MainNavigator />
              </NavigationContainer>
            );
          }}
        </AuthProvider>
      </ErrorBoundary>
    </AppWrapper>
  );
}

const styles = StyleSheet.create({
  appWrapper: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
  },
  appContainer: {
    flex: 1,
    width: '100%',
    maxWidth: layout.maxContentWidth,
    backgroundColor: '#FFFFFF',
  },
  splash: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#000000',
  },
  splashText: {
    marginTop: 16,
    fontSize: 32,
    fontWeight: 'bold',
    color: '#FFDD00',
  },
  error: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  errorText: {
    fontSize: 16,
    color: '#F44336',
  },
});

export default App;
